import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { IconDanger, IconSuccess } from '../../../core';
import commonPageContentStyles from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

import brandPrint from './brand-print.json';
import {
  BrandChip,
  BrandGroup,
  BrandName,
  BrandRow,
  BrandSpecs,
  BrandSwatchItem,
  GroupTitle,
  RampInfo,
  RampName,
  RampRow,
  RampStepItem,
  RampSteps,
  RampSummary,
  RoleSwatch,
  SequentialSwatch,
  TokenExamples,
  FontColorRules,
} from './Colors.styled';
import {
  activeCore,
  brandSwatches,
  contrastLabel,
  coreVar,
  describeRamp,
  labelContrast,
  labelVarFor,
  RAMPS,
  rampSteps,
} from './palette';

interface Props {
  className?: string;
}

// The References of the Updated frame (13627:142630, 13627:142637); the frame carries no URLs.
const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Government Usability Testing Resources:',
    cards: [
      {
        title: 'Usability testing on Digital.gov',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovUsabilityTesting,
      },
      {
        title: 'Inclusive design principles',
        subtitle: '[inclusivedesignprinciples.info]',
        linkUrl: REFERENCE_LINKS.inclusiveDesignPrinciples,
      },
    ],
  },
  {
    id: 2,
    title: 'Government Accessibility Resources:',
    cards: [
      {
        title: 'The Section 508 accessibility program',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508Program,
      },
      {
        title: 'Accessibility for Teams guide',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovAccessibilityForTeams,
      },
    ],
  },
];

const brandGroups = [
  { id: 'primary', title: 'Primary colors' },
  { id: 'accent', title: 'Accent colors' },
] as const;

const ColorsStructure = ({ className = '' }: Props) => {
  const core = activeCore();
  const swatches = brandSwatches(core, brandPrint);

  return (
    <div className={className}>
      <div className="content">
        <ContentHeaderStyled
          title="Colors"
          breadcrumbs={[
            { label: 'Branding Styleguide', url: '/branding-styleguide' },
            { label: 'Colors' },
          ]}
        />

        <div className="page-container">
          <div className="page-content">
            <div className="page-content-with-navigation-wrapper">
              <div className="page-content-with-navigation">
                <div className="page-content-item">
                  <h2 className="h1" id="overview">
                    Overview
                  </h2>
                  <h3 className="h3" id="definition">
                    Definition
                  </h3>
                  <p className="mb-40">
                    The Colors section of the DHCS design system establishes the foundation of our
                    visual identity through a purposeful and accessible color palette. These
                    guidelines ensure consistency, clarity, and inclusivity, enabling teams to
                    create designs that are visually appealing, user-friendly, and aligned with the
                    DHCS brand values.
                  </p>
                  <h3 className="h3" id="brand-colors">
                    Brand colors
                  </h3>
                  <p className="mb-40">
                    Built from the DHCS Visual Identity Guidelines. HEX for web, RGB for screen,
                    CMYK for print, and Pantone (PMS) for spot color.
                  </p>
                </div>
                {brandGroups.map((group) => (
                  <BrandGroup key={group.id}>
                    <GroupTitle>{group.title}</GroupTitle>
                    <BrandRow>
                      {swatches
                        .filter((swatch) => swatch.group === group.id)
                        .map((swatch) => (
                          <BrandSwatchItem key={swatch.key} data-testid="brand-swatch">
                            <BrandChip
                              data-testid="brand-chip"
                              $color={`var(${swatch.cssVar})`}
                              $bordered={swatch.bordered}
                            />
                            <BrandName>{swatch.name}</BrandName>
                            <BrandSpecs>
                              <span>HEX {swatch.hex}</span>
                              <span>RGB {swatch.rgb}</span>
                              <span>CMYK {swatch.cmyk}</span>
                              {swatch.pms && <span>PMS {swatch.pms}</span>}
                            </BrandSpecs>
                          </BrandSwatchItem>
                        ))}
                    </BrandRow>
                  </BrandGroup>
                ))}
                <div className="page-content-item">
                  <h3 className="h3" id="palettes">
                    Palettes
                  </h3>
                  <p className="mb-40">
                    Each brand color anchors a ramp — tints step toward white, shades toward
                    near-black. Contrast (WCAG 2.1) shows each swatch’s legible label color vs its
                    background, with AA / AAA rating.
                  </p>
                </div>
                {RAMPS.map((ramp) => {
                  const steps = rampSteps(core, ramp.name);

                  return (
                    <RampRow key={ramp.name} data-testid="ramp">
                      <RampInfo>
                        <RampName data-testid="ramp-name">{ramp.title}</RampName>
                        <RampSummary>{describeRamp(ramp, core)}</RampSummary>
                      </RampInfo>
                      <RampSteps $skipFirstColumn={steps.length > 0 && steps[0].step !== 0}>
                        {steps.map(({ step, hex }) => (
                          <RampStepItem
                            key={step}
                            data-testid="ramp-step"
                            $color={`var(${coreVar(ramp.name, step)})`}
                            $label={labelVarFor(hex, core)}
                            $bordered={step === 0}
                          >
                            <span className="step">{step}</span>
                            <span className="hex">{hex}</span>
                            <span className="contrast">
                              {contrastLabel(labelContrast(hex))}
                              {step === ramp.base && ' · base'}
                            </span>
                          </RampStepItem>
                        ))}
                      </RampSteps>
                    </RampRow>
                  );
                })}
                <h2 className="divider h1" id="specifications">
                  Specifications
                </h2>
                <div className="page-content-item">
                  <h3 className="h3" id="purpose">
                    Purpose
                  </h3>
                  <p className="mb-40">
                    The purpose of using color in the DHCS system is to enhance communication and
                    usability. Colors should convey clear messages or statuses that users can easily
                    interpret at a glance. Neutral tones provide versatile backgrounds, text, and
                    shapes, maintaining clarity while occasionally implying subtle meanings, such as
                    inactive or disabled states.
                  </p>
                </div>
                <table className="table table-colors mb-40">
                  <thead>
                    <tr>
                      <th style={{ width: '11.5rem' }}>Role</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div className="role-color">
                          <RoleSwatch className="color" $color="var(--color-neutral-500)" />
                          <span>Neutral</span>
                        </div>
                      </td>
                      <td>
                        Use for default text and secondary UI elements, such as secondary buttons or
                        navigation elements.
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div className="role-color">
                          <RoleSwatch className="color" $color="var(--color-primary-500)" />
                          <span>Primary</span>
                        </div>
                      </td>
                      <td>Use for primary actions or elements that communicate the DHCS brand.</td>
                    </tr>
                    <tr>
                      <td>
                        <div className="role-color">
                          <RoleSwatch className="color" $color="var(--color-error-500)" />
                          <span>Alert</span>
                        </div>
                      </td>
                      <td>
                        Use for informative UI, such as an information icon, or UI that communicates
                        something is in progress.
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div className="role-color">
                          <RoleSwatch className="color" $color="var(--color-warning-500)" />
                          <span>Warning</span>
                        </div>
                      </td>
                      <td>
                        Use for UI that communicates caution to prevent a mistake or error from
                        occurring.
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div className="role-color">
                          <RoleSwatch className="color" $color="var(--color-success-500)" />
                          <span>Success</span>
                        </div>
                      </td>
                      <td>Use to communicate a favorable outcome, such as a success message.</td>
                    </tr>
                    <tr>
                      <td>
                        <div className="role-color">
                          <SequentialSwatch className="color" />
                          <span>Sequential</span>
                        </div>
                      </td>
                      <td>
                        Use for for highlighting trends or variations in data without implying any
                        specific categories or grouping.
                      </td>
                    </tr>
                  </tbody>
                </table>

                <h3 className="h3" id="anatomy">
                  Anatomy
                </h3>
                <div className="page-content-item">
                  <p className="mb-40">
                    Neutral gray tones form the foundation of the design, using subtle value shifts
                    to create visual hierarchy and organize content effectively. A core blue serves
                    as the primary action color, ensuring consistent and intuitive interactions
                    across all experiences. Accent colors are applied purposefully and sparingly to
                    draw attention to key elements without disrupting the overall balance.
                  </p>
                </div>
                <figure>
                  <img
                    src="/images/branding-styleguide-colors/example-color-roles@2x.webp"
                    width={1000}
                    height={369}
                    alt="Example color roles"
                  />
                </figure>
                <h3 className="h3" id="tokens">
                  Tokens
                </h3>
                <div className="page-content-item mb-40">
                  <p className="mb-8">
                    For enabled UI elements, prioritize colors from the Gray 100 to Gray 70 range,
                    while text and icons should use shades between White and Gray 50.
                  </p>
                  <p>
                    In darker themes, such as Gray 90, the global background starts with Gray 90,
                    layered with components featuring Gray 80 for the first level, Gray 70 for the
                    second, and Gray 60 for the third. This structured approach creates depth,
                    clarity, and consistency across the interface.
                  </p>
                </div>
                <figure>
                  <img
                    src="/images/branding-styleguide-colors/applying-color-with-design-tokens@2x.webp"
                    width={1000}
                    height={563}
                    alt="Example color roles"
                  />
                  <figcaption>Applying color with design tokens</figcaption>
                </figure>

                <h3 className="h3" id="tokens-usage">
                  Tokens usage
                </h3>
                <div className="page-content-item mb-40">
                  <p className="mb-8">
                    For clarity and ease of use, the token name itself reflects its specific
                    purpose, streamlining the process of accurate application. The first part of the
                    token name identifies the UI element being styled, such as background, text, or
                    border, while the second part defines its unique role within that category, such
                    as $neutral/border or $primary/text.
                  </p>
                  <p>
                    In certain cases, an interaction state is added at the end, like
                    $neutral/bg-base, to indicate the token&apos;s behavior under different
                    conditions.
                  </p>
                </div>
                <figure className="figure-sm">
                  <TokenExamples>
                    <img
                      src="/images/branding-styleguide-colors/tokens-usage-input@2x.webp"
                      alt="A search input annotated with the neutral/bg-medium, neutral/border, neutral/bg-base and primary/text tokens"
                      width={488}
                      height={353}
                    />
                    <img
                      src="/images/branding-styleguide-colors/tokens-usage-chart@2x.webp"
                      alt="A polar chart annotated with the neutral/bg-base and data viz/qualitative 1 to 5 tokens"
                      width={488}
                      height={353}
                    />
                  </TokenExamples>
                  <figcaption>
                    Color tokens for components are the same across themes as shown by this text
                    input using the White theme (left) and Gray 100 theme (right).
                  </figcaption>
                </figure>

                <h2 className="divider h1" id="guidelines">
                  Guidelines
                </h2>
                <div className="page-content-item">
                  <h3 className="h3" id="usage">
                    Usage
                  </h3>
                  <p className="mb-40">
                    There are two light themes in Carbon: White and Gray 10. For enabled UI colors
                    light themes primarily use the color range of White to Gray 20, and for text and
                    icons uses the color range between Gray 100 and Gray 60. All of the themes are
                    available in Design kits.
                  </p>
                  <h3 className="h3" id="best-practices">
                    Best practices
                  </h3>
                  <p className="mb-40">
                    There are two light themes in Carbon: White and Gray 10. For enabled UI colors
                    light themes primarily use the color range of White to Gray 20, and for text and
                    icons uses the color range between Gray 100 and Gray 60. All of the themes are
                    available in Design kits.
                  </p>
                </div>
                <div className="ex-cards">
                  <div className="ex-card true">
                    <img
                      className="ex-card-img"
                      src="/images/branding-styleguide-colors/screen-white-dropdown-on-gray-background@2x.webp"
                      width={488}
                      height={409}
                      alt="White dropdown on gray background"
                    />
                    <div className="ex-card-desc">
                      <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                        Do
                      </BadgeStyled>
                      <p>White dropdown on gray background.</p>
                    </div>
                  </div>
                  <div className="ex-card false">
                    <img
                      src="/images/branding-styleguide-colors/screen-avoid-use-of-midtones@2x.webp"
                      width={488}
                      height={409}
                      alt="Avoid use of midtones"
                    />
                    <div className="ex-card-desc">
                      <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                        Don&apos;t
                      </BadgeStyled>
                      <p>Avoid use of midtones.</p>
                    </div>
                  </div>
                </div>
                <div className="page-content-item">
                  <h3 className="h3" id="states">
                    States
                  </h3>
                  <p className="mb-24">
                    In addition to the core set of enabled-state tokens, there are five other
                    interaction states defined with tokens for each theme. Interaction tokens are
                    signified by the addition of a state name added to the end of the base token
                    name. For example, the background hover state token is background-hover.
                  </p>
                </div>
                <img
                  src="/images/branding-styleguide-colors/examples-contrast@2x.webp"
                  width={1000}
                  height={468}
                  alt="Example contrast"
                  className="mb-40 img-responsive"
                />
                <div className="page-content-item mb-40">
                  <p>
                    <strong>Must pass 3:1 contrast:</strong> Any UI essential to understanding the
                    experience and text 24px
                    <br />
                    <strong>Must pass 4.5:1:</strong> Text smaller than 24px
                  </p>
                </div>
                <section className="colors-accessibility">
                  <h2 className="h1 mb-16" id="accessibility">
                    Accessibility
                  </h2>
                  <figure className="figure-sm">
                    <p className="text-body14 mb-0">
                      Color contrast impacts the readability of your content on the web and in
                      print. Check that colors chosen for actionable digital graphics — buttons,
                      hyperlinks, and icons — are AA contrast compliant. Use the chart below to
                      select adequately contrasting hues.
                    </p>
                    <img
                      src="/images/branding-styleguide-colors/accessibility-contrast@2x.webp"
                      alt="Contrasting color pairs: black, DHCS Blue and DHCS Teal on white; white and DHCS Yellow on DHCS Blue; white on DHCS Teal; black and DHCS Blue on orange and on DHCS Yellow"
                      width={1000}
                      height={400}
                    />
                  </figure>
                </section>
                <section className="colors-font-colors">
                  <h3 className="h3" id="font-colors">
                    Font Colors
                  </h3>
                  <FontColorRules>
                    <p>Heading Copy — DHCS Black</p>
                    <p>Body Copy — Black</p>
                    <p>Emphasized Body Copy — DHCS Teal, DHCS Blue, or Black</p>
                  </FontColorRules>
                  <FontColorRules className="mb-40">
                    <p>
                      Minimum font size is 12 pt unless an official document or template states
                      otherwise.
                    </p>
                  </FontColorRules>
                </section>
                <section className="colors-program-brand">
                  <h2 className="h1 mb-24" id="program-brand">
                    Program Brand System
                  </h2>
                  <p className="text-paragraph-medium mb-40">
                    Programs use a distinct brand system built on the DHCS foundation. The program
                    palette promotes DHCS Yellow to a primary color and moves DHCS Teal to a
                    limited-use accent, paired with a dedicated set of patterns, elements, and
                    illustration styles. Use no more than two brand elements on any one piece.
                  </p>
                </section>
                <h2 className="h1 divider" id="references">
                  References
                </h2>
                {dataExternalLinkGroups.map((externalLinkGroup) => (
                  <ExternalLinkGroupStyled
                    key={externalLinkGroup.id}
                    title={externalLinkGroup.title}
                    cards={externalLinkGroup.cards}
                  />
                ))}
              </div>
              <div>
                <div className="page-navigation">
                  <h2>Contents</h2>
                  <Scrollspy
                    ids={[
                      'overview',
                      'definition',
                      'brand-colors',
                      'palettes',
                      'specifications',
                      'purpose',
                      'anatomy',
                      'tokens',
                      'tokens-usage',
                      'guidelines',
                      'usage',
                      'best-practices',
                      'states',
                      'accessibility',
                      'font-colors',
                      'program-brand',
                    ]}
                    offset={0}
                    activeItemClassName="active"
                    itemContainerClassName="multilevel-link-list"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <PaginationStyled
          previousLink="/branding-styleguide"
          previous="Branding Styleguide"
          nextLink="/branding-styleguide/typography"
          next="Typography"
        />
      </div>
    </div>
  );
};

const Colors = styled(ColorsStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 3, 4, 6, 7, 8, 9, 11, 12, 13, 15])}
    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }

  /* The roles table rows are 80px: 24px above and below the 32px chip, the 1px stroke inside. */
  .table-colors td {
    padding-bottom: 1.4375rem;
  }
`;

export default Colors;
